import { useEffect, useRef } from "react";
import { useReminder, useFCMTokenManagement } from "./hooks/useReminder";
import { notificationService } from "./services/notificationService";
import { useAuth } from "./contexts/AuthContext";

// Retry wrapper
async function retry<T>(
  fn: () => Promise<T>,
  attempts: number,
  delayMs: number,
  label: string
): Promise<T | null> {
  for (let i = 0; i < attempts; i++) {
    try {
      const result = await fn();
      return result;
    } catch (err) {
      console.warn(
        `[Orchestrator] ${label} failed (attempt ${i + 1}/${attempts}):`,
        err
      );
      if (i < attempts - 1) {
        await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
      }
    }
  }
  console.error(`[Orchestrator] ${label} failed after ${attempts} attempts`);
  return null;
}

const NotificationOrchestrator = (): null => {
  const { currentUser } = useAuth();
  const { permissionState } = useReminder();
  const { tokens, loading: tokensLoading, addCurrentDevice } =
    useFCMTokenManagement();

  // hasRunRef prevents duplicate runs within the same React session
  // BUT for PWA: sessionStorage flag overrides this on every fresh launch
  const hasRunRef = useRef<boolean>(false);

  useEffect(() => {
    const run = async (): Promise<void> => {
      if (!currentUser) return;
      if (tokensLoading) return;
      if (!permissionState.granted) return;

      const isPWA: boolean =
        window.matchMedia("(display-mode: standalone)").matches ||
        Boolean((window.navigator as any).standalone);

      // Check if this is a fresh PWA launch (set by index.tsx before React renders)
      // sessionStorage is cleared on force-close, so this is always fresh on real launches
      const needsFreshRegistration =
        sessionStorage.getItem("pwa_needs_fresh_registration") === "true";

      // Gate logic:
      // Browser: run once per session (hasRunRef blocks duplicates)
      // PWA fresh launch: always run regardless of hasRunRef
      // PWA same session (backgrounded/foregrounded): respect hasRunRef
      if (!isPWA && hasRunRef.current) return;
      if (isPWA && !needsFreshRegistration && hasRunRef.current) return;

      hasRunRef.current = true;

      // Clear the flag immediately so we don't re-run again within same session
      // (e.g. if tokens reload causes useEffect to re-fire)
      if (isPWA && needsFreshRegistration) {
        sessionStorage.removeItem("pwa_needs_fresh_registration");
        console.log("[Orchestrator] Fresh PWA launch detected — will force token refresh");
      }

      console.log(
        `[Orchestrator] Starting — context: ${isPWA ? "PWA" : "Browser"}, freshLaunch: ${needsFreshRegistration}`
      );

      try {
        // Step 1: Get messaging ready with retry
        const messagingReady = await retry<boolean>(
          () => notificationService.waitForMessagingReady(),
          3,
          2000,
          "waitForMessagingReady"
        );

        if (!messagingReady) {
          console.warn("[Orchestrator] Messaging not ready after retries, aborting");
          hasRunRef.current = false;
          return;
        }

        // Step 2: Get the live token with retry
        const liveToken = await retry<string | null>(
          () => notificationService.getFCMToken(),
          3,
          2000,
          "getFCMToken"
        );

        if (!liveToken) {
          console.warn("[Orchestrator] Could not get live token, aborting");
          hasRunRef.current = false;
          return;
        }

        console.log("[Orchestrator] Live token obtained ✅");

        // Step 3: Check token vs what is stored in Firestore
        const deviceId: string = notificationService.getCurrentDeviceId();
        const deviceToken = tokens.find((t) => t.deviceId === deviceId);

        const tokenIsMissing: boolean = !deviceToken;
        const tokenIsStale: boolean =
          !!deviceToken && deviceToken.token !== liveToken;

        // Register if:
        // - device not found in Firestore at all
        // - token in Firestore doesn't match live FCM token
        // - fresh PWA launch (token may have rotated while app was closed)
        const shouldRegister: boolean =
          tokenIsMissing || tokenIsStale || needsFreshRegistration;

        console.log(
          `[Orchestrator] Token check — missing: ${tokenIsMissing}, stale: ${tokenIsStale}, freshPWALaunch: ${needsFreshRegistration}`
        );

        if (shouldRegister) {
          if (tokenIsMissing) {
            console.log("[Orchestrator] Device not found — registering...");
          } else if (tokenIsStale) {
            console.log("[Orchestrator] Token mismatch — refreshing...");
            console.log(
              "[Orchestrator] Stored:",
              deviceToken!.token.slice(0, 20) + "..."
            );
            console.log(
              "[Orchestrator] Live:  ",
              liveToken.slice(0, 20) + "..."
            );
          } else {
            console.log(
              "[Orchestrator] Fresh PWA launch — verifying token is current..."
            );
          }

          await retry<void>(
            () => addCurrentDevice(),
            3,
            2000,
            "registerDevice"
          );
          console.log("[Orchestrator] Device registered/refreshed ✅");
        } else {
          console.log(
            "[Orchestrator] Device registered with current token ✅"
          );
        }
      } catch (err) {
        console.error("[Orchestrator] Failed:", err);
        hasRunRef.current = false;
      }
    };

    run();
  }, [currentUser, tokensLoading, permissionState.granted, tokens, addCurrentDevice]);

  return null;
};

export default NotificationOrchestrator;